import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Megaphone,
  Plus,
  Eye,
  MousePointerClick,
  Percent,
  XCircle,
  Play,
  Pause,
  Copy,
  Trash2,
  ArrowLeft,
  X,
  Upload,
  Clock,
  Target,
  Calendar,
  BarChart3,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import api from "../api/client";

const TYPE_BADGES = {
  popup: { label: "Popup", bg: "bg-blue-900/30", text: "text-blue-400" },
  slide_in: { label: "Slide-in", bg: "bg-green-900/30", text: "text-green-400" },
  floating_bar: { label: "Float Bar", bg: "bg-orange-900/30", text: "text-orange-400" },
  inline: { label: "Inline", bg: "bg-purple-900/30", text: "text-purple-400" },
};

const STATUS_BADGES = {
  active: { bg: "bg-green-900/30", text: "text-green-400" },
  paused: { bg: "bg-amber-900/30", text: "text-amber-400" },
  draft: { bg: "bg-gray-800/50", text: "text-gray-400" },
};

const TRIGGER_LABELS = {
  time_delay: "Time Delay",
  scroll_percent: "Scroll %",
  exit_intent: "Exit Intent",
  immediate: "Immediate",
};

const FREQUENCY_LABELS = {
  once_per_session: "Once per session",
  once_per_day: "Once per day",
  once_ever: "Once ever",
  always: "Every page load",
};

// ─── Main Component ───────────────────────────────────────────

export default function Campaigns({ selectedSite }) {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isNew = searchParams.get("new") === "1";

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a website to manage campaigns.
      </div>
    );
  }

  if (editId || isNew) {
    return <CampaignEditor siteId={selectedSite.id} campaignId={editId ? parseInt(editId) : null} />;
  }

  return <CampaignList siteId={selectedSite.id} />;
}

// ─── Campaign List ────────────────────────────────────────────

function CampaignList({ siteId }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchCampaigns = useCallback(() => {
    setLoading(true);
    api
      .get(`/websites/${siteId}/campaigns/`)
      .then((r) => setCampaigns(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setError(e.response?.data?.error || "Failed to load campaigns"))
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleToggle = (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "paused" : "active";
    api.put(`/websites/${siteId}/campaigns/${id}/`, { status: newStatus })
      .then(() => fetchCampaigns())
      .catch(() => {});
  };

  const handleDuplicate = (campaign) => {
    const data = {
      title: campaign.title + " (Copy)",
      type: campaign.type,
      status: "draft",
      design: campaign.design,
      trigger_rules: campaign.trigger_rules,
      targeting_rules: campaign.targeting_rules,
      schedule: campaign.schedule,
    };
    api.post(`/websites/${siteId}/campaigns/`, data)
      .then(() => fetchCampaigns())
      .catch(() => {});
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this campaign and all its stats?")) return;
    api.put(`/websites/${siteId}/campaigns/${id}/`, { status: "draft" })
      .then(() => fetchCampaigns())
      .catch(() => {});
  };

  // Aggregate stats
  const activeCount = campaigns.filter((c) => c.status === "active").length;
  const totalImpressions = campaigns.reduce((s, c) => s + (c.impressions || 0), 0);
  const totalConversions = campaigns.reduce((s, c) => s + (c.conversions || 0), 0);
  const avgRate = totalImpressions > 0 ? ((totalConversions / totalImpressions) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Megaphone size={20} className="text-copper" /> Campaigns
        </h2>
        <button
          onClick={() => navigate("/campaigns?new=1")}
          className="flex items-center gap-1.5 px-4 py-2 bg-copper hover:bg-copper/90 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} /> New Campaign
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Megaphone} label="Active Campaigns" value={activeCount} color="text-copper" />
        <StatCard icon={Eye} label="Total Impressions" value={totalImpressions.toLocaleString()} color="text-blue-400" />
        <StatCard icon={MousePointerClick} label="Total Conversions" value={totalConversions.toLocaleString()} color="text-green-400" />
        <StatCard icon={Percent} label="Avg Conversion Rate" value={`${avgRate}%`} color="text-amber-400" />
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-900/20 border border-red-800/30 rounded-lg text-sm text-red-400">{error}</div>
      )}

      {/* Table */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-dark-50">
                <th className="text-left py-3 px-4 font-medium">Campaign</th>
                <th className="text-left py-3 px-4 font-medium">Type</th>
                <th className="text-left py-3 px-4 font-medium">Status</th>
                <th className="text-right py-3 px-4 font-medium">Impressions</th>
                <th className="text-right py-3 px-4 font-medium">Conversions</th>
                <th className="text-right py-3 px-4 font-medium">Rate</th>
                <th className="text-left py-3 px-4 font-medium">Created</th>
                <th className="text-right py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-500">Loading campaigns...</td></tr>
              ) : campaigns.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-500">No campaigns yet. Create one to start capturing leads.</td></tr>
              ) : (
                campaigns.map((c) => {
                  const typeBadge = TYPE_BADGES[c.type] || TYPE_BADGES.popup;
                  const statusBadge = STATUS_BADGES[c.status] || STATUS_BADGES.draft;
                  const rate = c.impressions > 0 ? ((c.conversions / c.impressions) * 100).toFixed(1) : "0.0";
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <button
                          onClick={() => navigate(`/campaigns?edit=${c.id}`)}
                          className="font-medium text-gray-200 hover:text-copper transition-colors text-left"
                        >
                          {c.title || "(untitled)"}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${typeBadge.bg} ${typeBadge.text}`}>
                          {typeBadge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${statusBadge.bg} ${statusBadge.text}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-300">{(c.impressions || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-mono text-green-400">{(c.conversions || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-mono text-amber-400">{rate}%</td>
                      <td className="py-3 px-4 text-xs text-gray-500">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => handleToggle(c.id, c.status)} className="p-1.5 hover:bg-dark-50 rounded-lg" title={c.status === "active" ? "Pause" : "Activate"}>
                            {c.status === "active" ? <Pause size={14} className="text-amber-400" /> : <Play size={14} className="text-green-400" />}
                          </button>
                          <button onClick={() => handleDuplicate(c)} className="p-1.5 hover:bg-dark-50 rounded-lg" title="Duplicate">
                            <Copy size={14} className="text-gray-400" />
                          </button>
                          <button onClick={() => handleDelete(c.id)} className="p-1.5 hover:bg-dark-50 rounded-lg" title="Delete">
                            <Trash2 size={14} className="text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Shortcode hint */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
        <p className="text-xs text-gray-500">
          Use <code className="bg-dark-50 px-1.5 py-0.5 rounded text-gray-300">[onesuiteseo_campaign id="X"]</code> in any WordPress post or page to embed an inline campaign.
        </p>
      </div>
    </div>
  );
}

// ─── Campaign Editor ──────────────────────────────────────────

function CampaignEditor({ siteId, campaignId }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("design");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!campaignId);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    title: "",
    type: "popup",
    status: "draft",
    design: {
      headline: "",
      description: "",
      button_text: "Get Started",
      success_message: "Thank you! We'll be in touch.",
      bg_color: "#ffffff",
      text_color: "#333333",
      button_color: "#b87333",
      button_text_color: "#ffffff",
      image_url: "",
      position: "center",
      template: "modern",
      show_name: true,
      show_phone: true,
      show_message: false,
      name_required: false,
      phone_required: false,
    },
    trigger_rules: {
      trigger_type: "time_delay",
      time_delay: 5,
      scroll_percent: 50,
      frequency: "once_per_session",
    },
    targeting_rules: [],
    schedule: { start_date: "", end_date: "" },
  });

  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${siteId}/campaigns/${campaignId}/`),
      api.get(`/websites/${siteId}/campaigns/${campaignId}/stats/`).catch(() => null),
    ])
      .then(([campaignRes, statsRes]) => {
        const c = campaignRes.data;
        setForm({
          title: c.title || "",
          type: c.type || "popup",
          status: c.status || "draft",
          design: { ...form.design, ...(c.design || {}) },
          trigger_rules: { ...form.trigger_rules, ...(c.trigger_rules || {}) },
          targeting_rules: c.targeting_rules || [],
          schedule: c.schedule || { start_date: "", end_date: "" },
        });
        if (statsRes?.data) setStats(statsRes.data);
      })
      .catch((e) => setError(e.response?.data?.error || "Failed to load campaign"))
      .finally(() => setLoading(false));
  }, [siteId, campaignId]);

  const handleSave = () => {
    if (!form.title.trim()) { setError("Campaign title is required."); return; }
    setSaving(true);
    setError(null);

    const request = campaignId
      ? api.put(`/websites/${siteId}/campaigns/${campaignId}/`, form)
      : api.post(`/websites/${siteId}/campaigns/`, form);

    request
      .then((r) => {
        setSuccess("Campaign saved.");
        setTimeout(() => setSuccess(null), 3000);
        if (!campaignId && r.data?.id) {
          navigate(`/campaigns?edit=${r.data.id}`, { replace: true });
        }
      })
      .catch((e) => setError(e.response?.data?.error || "Failed to save campaign"))
      .finally(() => setSaving(false));
  };

  const updateDesign = (key, value) => setForm({ ...form, design: { ...form.design, [key]: value } });
  const updateTrigger = (key, value) => setForm({ ...form, trigger_rules: { ...form.trigger_rules, [key]: value } });
  const updateSchedule = (key, value) => setForm({ ...form, schedule: { ...form.schedule, [key]: value } });

  const addTargetingRule = () => {
    setForm({ ...form, targeting_rules: [...form.targeting_rules, { type: "url_path", operator: "contains", value: "" }] });
  };
  const removeTargetingRule = (index) => {
    setForm({ ...form, targeting_rules: form.targeting_rules.filter((_, i) => i !== index) });
  };
  const updateTargetingRule = (index, key, value) => {
    const rules = [...form.targeting_rules];
    rules[index] = { ...rules[index], [key]: value };
    setForm({ ...form, targeting_rules: rules });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-dark-300 rounded-lg w-64" />
        <div className="h-96 bg-dark-300 rounded-xl" />
      </div>
    );
  }

  const tabs = [
    { key: "design", label: "Design", icon: Megaphone },
    { key: "triggers", label: "Triggers", icon: Clock },
    { key: "targeting", label: "Targeting", icon: Target },
    { key: "schedule", label: "Schedule", icon: Calendar },
    ...(campaignId ? [{ key: "stats", label: "Stats", icon: BarChart3 }] : []),
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/campaigns")} className="p-2 hover:bg-dark-50/50 rounded-lg">
          <ArrowLeft size={18} className="text-gray-400" />
        </button>
        <h2 className="text-lg font-semibold">{campaignId ? "Edit Campaign" : "New Campaign"}</h2>
      </div>

      {error && <div className="p-3 bg-red-900/20 border border-red-800/30 rounded-lg text-sm text-red-400">{error}</div>}
      {success && <div className="p-3 bg-green-900/20 border border-green-800/30 rounded-lg text-sm text-green-400">{success}</div>}

      {/* Stats cards */}
      {stats?.summary && (
        <div className="grid grid-cols-4 gap-4">
          <StatCard icon={Eye} label="Impressions (30d)" value={stats.summary.impressions?.toLocaleString()} color="text-blue-400" />
          <StatCard icon={MousePointerClick} label="Conversions (30d)" value={stats.summary.conversions?.toLocaleString()} color="text-green-400" />
          <StatCard icon={Percent} label="Conversion Rate" value={`${stats.summary.conversion_rate}%`} color="text-amber-400" />
          <StatCard icon={XCircle} label="Closes (30d)" value={stats.summary.closes?.toLocaleString()} color="text-red-400" />
        </div>
      )}

      {/* Title / Type / Status row */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Campaign Title</label>
            <input
              type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Homepage Popup"
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50">
              <option value="popup">Popup</option>
              <option value="slide_in">Slide-in</option>
              <option value="floating_bar">Floating Bar</option>
              <option value="inline">Inline</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
        <div className="flex border-b border-dark-50 px-4">
          {tabs.map((t) => (
            <button
              key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm border-b-2 transition-colors ${
                tab === t.key ? "border-copper text-copper font-medium" : "border-transparent text-gray-500 hover:text-gray-300"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "design" && <DesignTab design={form.design} updateDesign={updateDesign} />}
          {tab === "triggers" && <TriggersTab trigger={form.trigger_rules} updateTrigger={updateTrigger} />}
          {tab === "targeting" && <TargetingTab rules={form.targeting_rules} addRule={addTargetingRule} removeRule={removeTargetingRule} updateRule={updateTargetingRule} />}
          {tab === "schedule" && <ScheduleTab schedule={form.schedule} updateSchedule={updateSchedule} />}
          {tab === "stats" && stats && <StatsTab stats={stats} />}
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave} disabled={saving}
        className="w-full py-3 bg-copper hover:bg-copper/90 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save Campaign"}
      </button>

      {campaignId && (
        <p className="text-xs text-gray-500 text-center">
          Shortcode: <code className="bg-dark-50 px-1.5 py-0.5 rounded text-gray-300">[onesuiteseo_campaign id="{campaignId}"]</code>
        </p>
      )}
    </div>
  );
}

// ─── Tab Panels ───────────────────────────────────────────────

function DesignTab({ design, updateDesign }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Headline</label>
          <input type="text" value={design.headline} onChange={(e) => updateDesign("headline", e.target.value)} placeholder="Get a Free Quote Today!" className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Button Text</label>
          <input type="text" value={design.button_text} onChange={(e) => updateDesign("button_text", e.target.value)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Description</label>
        <textarea value={design.description} onChange={(e) => updateDesign("description", e.target.value)} rows={3} placeholder="Brief description or offer details..." className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Success Message</label>
        <input type="text" value={design.success_message} onChange={(e) => updateDesign("success_message", e.target.value)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Image URL (optional)</label>
        <input type="url" value={design.image_url} onChange={(e) => updateDesign("image_url", e.target.value)} placeholder="https://..." className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Position</label>
          <select value={design.position} onChange={(e) => updateDesign("position", e.target.value)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50">
            <option value="center">Center</option>
            <option value="bottom-right">Bottom Right</option>
            <option value="bottom-left">Bottom Left</option>
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Template</label>
          <select value={design.template} onChange={(e) => updateDesign("template", e.target.value)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50">
            <option value="modern">Modern</option>
            <option value="minimal">Minimal</option>
            <option value="bold">Bold</option>
          </select>
        </div>
      </div>

      {/* Colors */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Colors</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key: "bg_color", label: "Background" },
            { key: "text_color", label: "Text" },
            { key: "button_color", label: "Button" },
            { key: "button_text_color", label: "Button Text" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2">
              <input type="color" value={design[key]} onChange={(e) => updateDesign(key, e.target.value)} className="w-8 h-8 rounded border border-dark-50 cursor-pointer bg-transparent" />
              <span className="text-xs text-gray-400">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Form Fields */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Form Fields</label>
        <p className="text-xs text-gray-500 mb-3">Email is always shown and required.</p>
        <div className="space-y-2">
          {[
            { showKey: "show_name", reqKey: "name_required", label: "Name" },
            { showKey: "show_phone", reqKey: "phone_required", label: "Phone" },
            { showKey: "show_message", reqKey: null, label: "Message" },
          ].map(({ showKey, reqKey, label }) => (
            <div key={showKey} className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
                <input type="checkbox" checked={design[showKey]} onChange={(e) => updateDesign(showKey, e.target.checked)} className="accent-copper" />
                Show {label}
              </label>
              {reqKey && (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500">
                  <input type="checkbox" checked={design[reqKey]} onChange={(e) => updateDesign(reqKey, e.target.checked)} className="accent-copper" />
                  Required
                </label>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TriggersTab({ trigger, updateTrigger }) {
  return (
    <div className="space-y-5 max-w-lg">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Show campaign when...</label>
        <select value={trigger.trigger_type} onChange={(e) => updateTrigger("trigger_type", e.target.value)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50">
          {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {trigger.trigger_type === "time_delay" && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Delay (seconds)</label>
          <input type="number" min={0} max={300} value={trigger.time_delay} onChange={(e) => updateTrigger("time_delay", parseInt(e.target.value) || 0)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
        </div>
      )}
      {trigger.trigger_type === "scroll_percent" && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Scroll percentage: {trigger.scroll_percent}%</label>
          <input type="range" min={0} max={100} value={trigger.scroll_percent} onChange={(e) => updateTrigger("scroll_percent", parseInt(e.target.value))} className="w-full accent-copper" />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Frequency</label>
        <select value={trigger.frequency} onChange={(e) => updateTrigger("frequency", e.target.value)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50">
          {Object.entries(FREQUENCY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
    </div>
  );
}

function TargetingTab({ rules, addRule, removeRule, updateRule }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">Leave empty to show on all pages. Add rules to target specific pages (OR logic).</p>
      {rules.map((rule, i) => (
        <div key={i} className="flex items-center gap-2 bg-dark/50 border border-dark-50 rounded-lg p-3">
          <select value={rule.type} onChange={(e) => updateRule(i, "type", e.target.value)} className="bg-dark border border-dark-50 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-copper/50">
            <option value="url_path">URL Path</option>
            <option value="full_url">Full URL</option>
            <option value="post_type">Post Type</option>
            <option value="category">Category</option>
            <option value="tag">Tag</option>
            <option value="referrer">Referrer</option>
            <option value="regex">Regex</option>
          </select>
          <select value={rule.operator} onChange={(e) => updateRule(i, "operator", e.target.value)} className="bg-dark border border-dark-50 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-copper/50">
            <option value="contains">Contains</option>
            <option value="equals">Equals</option>
            <option value="not_equals">Not Equals</option>
            <option value="not_contains">Not Contains</option>
            <option value="starts_with">Starts With</option>
            <option value="ends_with">Ends With</option>
          </select>
          <input type="text" value={rule.value} onChange={(e) => updateRule(i, "value", e.target.value)} placeholder="Value..." className="flex-1 bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
          <button onClick={() => removeRule(i)} className="p-1.5 hover:bg-dark-50 rounded-lg">
            <X size={16} className="text-red-400" />
          </button>
        </div>
      ))}
      <button onClick={addRule} className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-dark-50 hover:border-copper/50 rounded-lg text-sm text-gray-400 hover:text-copper transition-colors">
        <Plus size={14} /> Add Rule
      </button>
    </div>
  );
}

function ScheduleTab({ schedule, updateSchedule }) {
  return (
    <div className="space-y-4 max-w-lg">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">Start Date (optional)</label>
        <input type="date" value={schedule.start_date} onChange={(e) => updateSchedule("start_date", e.target.value)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">End Date (optional)</label>
        <input type="date" value={schedule.end_date} onChange={(e) => updateSchedule("end_date", e.target.value)} className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
      </div>
    </div>
  );
}

function StatsTab({ stats }) {
  // Build daily chart data
  const dailyData = {};
  (stats.daily || []).forEach((row) => {
    if (!dailyData[row.date]) dailyData[row.date] = { date: row.date, impressions: 0, conversions: 0 };
    if (row.event_type === "impression") dailyData[row.date].impressions = row.cnt;
    if (row.event_type === "conversion") dailyData[row.date].conversions = row.cnt;
  });
  const chartData = Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6">
      {chartData.length > 0 ? (
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-3">Daily Performance (Last {stats.period_days} days)</h4>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} tickFormatter={(d) => new Date(d + "T00:00").toLocaleDateString("en", { month: "short", day: "numeric" })} interval={Math.max(0, Math.floor(chartData.length / 8))} />
              <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="impressions" stroke="#3b82f6" strokeWidth={2} dot={false} name="Impressions" />
              <Line type="monotone" dataKey="conversions" stroke="#22c55e" strokeWidth={2} dot={false} name="Conversions" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-8">No stats data available yet.</p>
      )}
    </div>
  );
}

// ─── Shared Components ────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-dark-50/50 rounded-lg">
          <Icon size={18} className={color} />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className="text-2xl font-mono font-bold">{value ?? "—"}</div>
    </div>
  );
}
